import gql from 'graphql-tag';
import { useQuery } from '@vue/apollo-composable';
import { reactive, watch, ref, computed } from 'vue';
import useDecryptSymbols from './useDecryptSymbols';
import useAlerts from './useAlerts';
import useCurrentUser from './useCurrentUser';
import useUsersQuery from './useUsersQuery';
import useSingleLeadQuery from './useSingleLeadQuery';

const { user } = useCurrentUser();
const { salespeople, allUsers } = useUsersQuery();
const { decryptSymbols } = useDecryptSymbols();
const { addAlert } = useAlerts();
const lead = useSingleLeadQuery.lead;

const leads = reactive([]);
const BROAD_SEARCH_EXCLUDED_SALESPERSON_NAMES = ['N/A', 'Out of Service Range'];
const BROAD_SEARCH_EXCLUDED_SALESPERSON_IDS = ['3CFBBC67-7E10-4644-8105-DC4214A74FD2'];
const filters = reactive({
  complete: false,
  dateFrom: null,
  dateTo: null,
  salespersonId: null,
  delivery: false,
  location: 'All',
  scores: ['hot', 'warm', 'cool'], // Default to show hot, warm, and cool but not dead
  searchAllSalespeople: false,
});

// If this is updated make sure to update the server side socket query as well
const GET_LEADS = gql`
  query LeadsQuery($salespersonId: String, $complete: Boolean, $dateFrom: String, $dateTo: String, $delivery: Boolean, $scores: [String]) {
    leads(salespersonId: $salespersonId, complete: $complete, dateFrom: $dateFrom, dateTo: $dateTo, delivery: $delivery, scores: $scores) {
      LEADID
      ASSIGNEDID
      NAME
      NOTES
      BWQUOTE
      CREATEDATE
      JOBNAME
      SOURCE
      LINKMETHOD
      COMPLETE
      INVNUM
      AMOUNT
      COMPLETEDATE
      COMPANYID
      CONTACTID
      HASDELIVERY
      DELIVERYADDRESS
      DELIVERYDATE
      GATECODE
      CHANNEL
      ALERTDELAY
      DELIVERYNOTES
      QUOTESUMMARY
      QUOTEDETAILS
      SCORE
      SUGGESTEDSALESPERSON
      address {
        LINE1
        LINE2
        CITY
        STATE
        POSTALCODE
      }
      email {
        ADDRESS
      }
      phones {
        NUMBERDISPLAY
        TYPEID
        EXT
      }
      interactions {
        INTERACTIONID
        DATE
        NOTE
        TYPE
        SALESPERSONID
      }
      company {
        NAME
        address {
          LINE1
          LINE2
          CITY
          STATE
          POSTALCODE
        }
        phones {
          NUMBERDISPLAY
          TYPEID
          EXT
        }
      }
      contact {
        FIRSTNAME
        LASTNAME
        address {
          LINE1
          LINE2
          CITY
          STATE
          POSTALCODE
        }
        company {
          NAME
        }
        email {
          ADDRESS
        }
        phones {
          NUMBERDISPLAY
          EXT
          TYPEID
        }
      }
    }
  }
`;

const refetchLeads = ref(null);
const loadingLeads = ref(false);

function setSalesperson() {
  if (user.userid && (user.insideSales || user.outsideSales)) {
    filters.salespersonId = user.userid;
  } else {
    filters.salespersonId = null;
  }
}

setSalesperson();

watch(
  user,
  () => {
    setSalesperson();
  },
  { deep: true }
);

watch(filters, () => {
  runFilters();
});

watch(leads, () => {
  runFilters();
});

watch(allUsers, () => {
  runFilters();
});

const salespersonLocations = computed(() => {
  const locations = {};

  salespeople.forEach((person) => {
    if (!locations[person.LOCATION]) {
      locations[person.LOCATION] = [];
    }
    locations[person.LOCATION].push(person);
  });
  return locations;
});

const normalizedBroadSearchExcludedSalespersonNames = BROAD_SEARCH_EXCLUDED_SALESPERSON_NAMES.map((name) => name.toLowerCase());

const normalizeId = (id) => id?.toString().toUpperCase();

const broadSearchExcludedSalespersonIds = computed(() => {
  const salespersonIds = new Set(BROAD_SEARCH_EXCLUDED_SALESPERSON_IDS.map(normalizeId).filter(Boolean));

  allUsers.forEach((person) => {
    if (normalizedBroadSearchExcludedSalespersonNames.includes(person.DISPLAYNAME?.trim().toLowerCase())) {
      const salespersonId = normalizeId(person.USERID);
      if (salespersonId) salespersonIds.add(salespersonId);
    }
  });

  return salespersonIds;
});

function isBroadSearchExcludedSalespersonId(salespersonId) {
  return broadSearchExcludedSalespersonIds.value.has(normalizeId(salespersonId));
}

function shouldSearchAcrossSalespeople() {
  return filters.searchAllSalespeople && !isBroadSearchExcludedSalespersonId(filters.salespersonId);
}

function getLeadQueryVariables() {
  return {
    salespersonId: shouldSearchAcrossSalespeople() ? null : filters.salespersonId,
    complete: filters.complete,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    delivery: filters.delivery,
    scores: filters.scores,
  };
}

function runFilters() {
  for (let index = leads.length - 1; index >= 0; index--) {
    const lead = leads[index];

    if (lead.COMPLETE && !filters.complete) {
      leads.splice(index, 1);
      continue;
    }

    if (filters.dateFrom && new Date(filters.dateFrom) > new Date(parseInt(lead.CREATEDATE) || lead.CREATEDATE)) {
      leads.splice(index, 1);
      continue;
    }

    if (filters.dateTo) {
      const dateTo = new Date(filters.dateTo);
      dateTo.setDate(dateTo.getDate() + 1); // add 1 day
      if (dateTo < new Date(parseInt(lead.CREATEDATE) || lead.CREATEDATE)) {
        leads.splice(index, 1);
        continue;
      }
    }

    if (!shouldSearchAcrossSalespeople()) {
      if (user.leadsAdmin) {
        if (filters.salespersonId && lead.ASSIGNEDID != filters.salespersonId && lead.ASSIGNEDID) {
          leads.splice(index, 1);
          continue;
        }
      } else {
        if (filters.salespersonId && lead.ASSIGNEDID != filters.salespersonId) {
          leads.splice(index, 1);
          continue;
        }
      }
    }

    if (filters.location != 'All' && lead.ASSIGNEDID && !salespersonLocations.value[filters.location]?.some((person) => person.USERID == lead.ASSIGNEDID)) {
      leads.splice(index, 1);
      continue;
    }

    if (filters.delivery && !lead.HASDELIVERY) {
      leads.splice(index, 1);
      continue;
    }

    if (filters.scores && filters.scores.length > 0 && !filters.scores.includes(lead.SCORE)) {
      leads.splice(index, 1);
      continue;
    }
  }
}

const useLeadsQuery = function () {
  const { result, error, refetch, loading } = useQuery(
    GET_LEADS,
    getLeadQueryVariables,
    { fetchPolicy: 'no-cache' }
  );

  refetchLeads.value = refetch;
  loadingLeads.value = loading;

  watch(result, () => {
    if (result.value) {
      const decrypted = decryptSymbols(result.value?.leads);
      leads.splice(0, leads.length, ...decrypted);
    }
  });

  watch(error, () => {
    if (error.value) {
      console.error('Error fetching leads:', error.value);
      addAlert('Error fetching leads', 'high', 4);
    }
  });
};

const leadsIncludingSingleLead = computed(() => {
  if (lead.value && !leads.some((l) => l.LEADID == lead.value.LEADID)) {
    return [...leads, lead.value];
  }
  return leads;
});

export default {
  useLeadsQuery,
  leadsIncludingSingleLead,
  leads,
  refetchLeads,
  loadingLeads,
  filters,
  broadSearchExcludedSalespersonIds,
  BROAD_SEARCH_EXCLUDED_SALESPERSON_NAMES,
};
